import { formatBytes } from '../core/format';
import { executeDeletion, planDeletion, type DeletionPlan } from './deletion';
import type { LibraryFootage } from './library';

export interface DeleterDom {
  readonly dialog: HTMLDialogElement;
  readonly title: HTMLElement;
  readonly text: HTMLElement;
  readonly warning: HTMLElement;
  readonly confirm: HTMLButtonElement;
}

export interface DeleteTarget {
  readonly footage: LibraryFootage;
  readonly title: string;
  readonly incident: string | undefined;
}

export interface DeleterHooks {
  readonly hide: (footage: LibraryFootage) => void;
  readonly restore: (footage: LibraryFootage) => void;
  readonly notify: (message: string) => void;
}

async function writable(root: FileSystemDirectoryHandle): Promise<boolean> {
  if ((await root.queryPermission?.({ mode: 'readwrite' })) === 'granted') return true;
  return (await root.requestPermission?.({ mode: 'readwrite' })) === 'granted';
}

export class Deleter {
  constructor(
    private readonly dom: DeleterDom,
    private readonly hooks: DeleterHooks,
  ) {}

  async request(root: FileSystemDirectoryHandle, target: DeleteTarget): Promise<void> {
    const plan = planDeletion(target.footage, root.name);
    if (!plan) return this.hooks.notify('This recording is outside the opened folder.');
    if (!(await this.confirm(target, plan))) return undefined;
    if (!(await writable(root))) return this.hooks.notify('Deleting needs permission to change this folder.');
    this.hooks.hide(target.footage);
    try {
      const result = await executeDeletion(root, plan);
      this.hooks.notify(result.leftovers.length ? `Deleted · ${formatBytes(result.bytes)} freed · folder kept for ${result.leftovers.join(', ')}` : `Deleted · ${formatBytes(result.bytes)} freed`);
    } catch (error) {
      this.hooks.restore(target.footage);
      this.hooks.notify(`Could not delete: ${error instanceof Error ? error.message : String(error)}`);
    }
    return undefined;
  }

  private confirm(target: DeleteTarget, plan: DeletionPlan): Promise<boolean> {
    const { dialog } = this.dom;
    this.dom.title.textContent = target.incident ? 'Delete this incident?' : 'Delete this recording?';
    this.dom.text.textContent = `${target.title} · every angle and every minute · ${plan.files.length} files · ${formatBytes(plan.bytes)}`;
    this.dom.warning.textContent = target.incident ? `It is marked as ${target.incident}. ` : '';
    dialog.returnValue = '';
    dialog.showModal();
    this.dom.confirm.focus();
    return new Promise((resolve) => dialog.addEventListener('close', () => resolve(dialog.returnValue === 'delete'), { once: true }));
  }
}
